import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, AlertTriangle, MapPin } from "lucide-react";
import api from "@/lib/api";
import { fmtDate, fmtTime, Loading, EmptyState, StatusBadge } from "@/components/common";
import Section from "@/components/Section";

export default function AdminGiri() {
  const [data, setData] = useState(null);

  const load = useCallback((date = "") => {
    api.get(`/admin/giri${date ? `?date=${date}` : ""}`).then((r) => setData(r.data)).catch(() => setData({ giri: [] }));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return <Loading />;

  return (
    <div data-testid="admin-giri">
      <h2 className="font-serif text-2xl text-navy mb-1">Giri di sabato</h2>
      <p className="text-sm text-muted-soft mb-6">
        Interventi del sabato raggruppati per comune. Oltre {data.limit} interventi nello stesso comune serve una gestione manuale.
      </p>

      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <button data-testid="giri-prev" onClick={() => load(data.prev)}
          className="p-3 min-h-[44px] min-w-[44px] rounded-full border border-[#dbe7f3] bg-white text-[#1e3a5f]" aria-label="Sabato precedente">
          <ChevronLeft size={20} />
        </button>
        <p className="font-semibold text-navy capitalize" data-testid="giri-date-label">
          Sabato {data.date ? fmtDate(data.date + "T12:00:00") : "—"}
        </p>
        <button data-testid="giri-next" onClick={() => load(data.next)}
          className="p-3 min-h-[44px] min-w-[44px] rounded-full border border-[#dbe7f3] bg-white text-[#1e3a5f]" aria-label="Sabato successivo">
          <ChevronRight size={20} />
        </button>
      </div>

      {data.giri.length === 0 ? (
        <EmptyState title="Nessun intervento in programma per questo sabato" />
      ) : (
        <div className="space-y-4">
          {data.giri.map((g, gi) => (
            <Section
              key={g.comune}
              title={`Giro ${g.comune}`}
              count={g.count}
              icon={MapPin}
              testid={`giro-admin-comune-${g.comune}`}
              defaultOpen={gi === 0 || g.over_limit}
              badge={g.over_limit ? (
                <span className="text-[10px] font-bold uppercase tracking-wide text-red-800 bg-red-100 rounded-full px-2 py-0.5 shrink-0" data-testid={`giro-admin-overbadge-${g.comune}`}>
                  Oltre il limite
                </span>
              ) : null}
            >
              {g.pros.length > 0 && (
                <p className="text-xs text-muted-soft mb-3">Professionista: {g.pros.join(", ")}</p>
              )}
              {g.over_limit && (
                <p className="flex items-center gap-2 text-xs font-semibold text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3"
                  data-testid={`giro-admin-warning-${g.comune}`}>
                  <AlertTriangle size={14} className="shrink-0" />
                  Oltre il limite di {data.limit} interventi: valuta uno spostamento manuale dalla sezione Interventi.
                </p>
              )}
              <div className="space-y-2">
                {g.items.map((i, idx) => (
                  <div key={i.id} data-testid={`giro-admin-item-${i.id}`}
                    className="flex items-center gap-3 py-2.5 min-h-[44px] border-b border-[#f1f5f9] last:border-0">
                    <span className="w-7 h-7 rounded-full bg-[#4a7c59] text-white text-xs font-bold flex items-center justify-center shrink-0">{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-navy text-sm truncate">{fmtTime(i.scheduled_date)} — {i.customer_name}</p>
                      <p className="text-xs text-muted-soft truncate">
                        {i.service} · {i.cemetery?.name}{i.grave ? ` · ${i.grave.deceased_name} ${i.grave.deceased_surname}` : ""}
                      </p>
                    </div>
                    <StatusBadge status={i.status} />
                  </div>
                ))}
              </div>
            </Section>
          ))}
        </div>
      )}
    </div>
  );
}
